import { getCounters } from "../../../services/counter.service";
import { getBalance } from "../../../services/counter/wallet.service";
import { getCounterUsers } from "../../../services/user.service";
import {
    isAllowUserPermission,
    isCounterUser,
    isPrepaidCounterUser,
} from "../../../utility/authHelper";
import { userCompanyId, userCounterId } from "../../../utility/helpers";
import { getFormattedDate, hasPermission } from "../../../utility/utils";

const getTopNavbarMenus = () => {
    const _isCounterUser = isCounterUser();

    const menus = [
        {
            title: "Dashboard",
            url: "/",
            hasPermission: true,
        },
        {
            title: "Trip Active/Inactive",
            url: "/trip-list",
            hasPermission:
                (!_isCounterUser && hasPermission("trip-list-list")) ||
                isAllowUserPermission("can_update_activeness_of_trip", true),
        },
        {
            title: "Board",
            url: "/counter-admin/board",
            hasPermission:
                (!_isCounterUser && hasPermission("counterUser-board")) ||
                isAllowUserPermission("board", true),
        },
        {
            title: "Migration",
            url: "/counter-admin/seat-migration",
            hasPermission:
                (!_isCounterUser &&
                    hasPermission("counterUser-seat-migration")) ||
                isAllowUserPermission("seat_transfer", true),
        },
        {
            title: "Fund Request",
            url: "/counter-panel/fund-request",
            hasPermission: isPrepaidCounterUser(),
        },
        {
            title: "Reports",
            url: "#",
            hasPermission: hasPermission("reports"),
            subMenus: [
                {
                    title: "User Wise Sales",
                    url: "/counter-admin/reports-user-wise-sales",
                    hasPermission: hasPermission(
                        "reports-counter-user-wise-sales"
                    ),
                },
                {
                    title: "Counter Wise Sales",
                    url: "/counter-admin/reports-counter-wise-sales",
                    hasPermission: hasPermission("reports-counter-wise-sales"),
                },
                {
                    title: "New Counter Wise Sales",
                    url: "/counter-admin/reports-new-counter-wise-sales",
                    hasPermission: hasPermission(
                        "reports-new-counter-wise-sales"
                    ),
                },

                {
                    title: "User Wise Sales Summary",
                    url: "/counter-admin/reports-user-wise-sales-summary",
                    hasPermission: hasPermission(
                        "reports-counter-user-wise-sales-summary"
                    ),
                },
                {
                    title: "Counter Prepaid Balance",
                    url: "/counter-admin/reports-counter-prepaid-balance",
                    hasPermission:
                        isPrepaidCounterUser() &&
                        hasPermission("reports-counter-prepaid-balance"),
                },
            ],
        },
        {
            title: "My Account",
            url: "/profile",
            hasPermission: true,
        },
    ];

    const filteredMenus = [];

    menus.forEach((menu) => {
        if (menu.hasPermission) {
            if (menu.subMenus) {
                menu.subMenus = menu.subMenus.filter(
                    (subMenu) => subMenu.hasPermission
                );
            }

            filteredMenus.push(menu);
        }
    });

    return filteredMenus;
};

const TripSearchModel = () => ({
    from: {
        value: "",
        label: "Select an station",
    },
    to: {
        value: "",
        label: "Select an station",
    },
    departureDate: {
        value: getFormattedDate(new Date(), "dateiso"),
    },
    departureTime: {
        value: "",
        label: "Select time",
    },
    companyId: {
        value: "",
        label: "Select an company",
    },
    counterId: {
        value: "",
        label: "Select a counter",
    },
    counterUserId: {
        value: "",
        label: "Select a user",
    },
    tripNo: {
        value: "",
        label: "Select an trip",
    },
});

const TripSearchDropdownDefaultOptions = {
    fromStations: [],
    toStations: [],
    companies: [],
    counters: [],
    counterUsers: [],
};

const tripSearchSelectInputStyle = {
    container: (provided) => ({
        ...provided,
        width: "100%", // Adjust width as needed
    }),
    control: (provided) => ({
        ...provided,
        minHeight: "30px", // Adjust the height of the control
        height: "30px",
        fontSize: "14px", // Adjust font size
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: "0 6px", // Adjust padding to fit the smaller size
    }),
    singleValue: (provided) => ({
        ...provided,
        fontSize: "14px", // Match font size with control
    }),
    menu: (provided) => ({
        ...provided,
        fontSize: "14px", // Adjust font size in the menu
        width: "auto", // Adjust width based on content
        minWidth: "100%", // Ensure it is at least as wide as the input
        whiteSpace: "nowrap", // Prevent wrapping of text
    }),
    option: (provided) => ({
        ...provided,
        fontSize: "14px", // Adjust font size for options
        whiteSpace: "nowrap", // Prevent wrapping inside options
    }),
    placeholder: (provided) => ({
        ...provided,
        fontSize: "14px", // Adjust font size for placeholder
    }),
    indicatorSeparator: (provided) => ({
        ...provided,
        display: "none", // Optionally hide the indicator separator
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        padding: "4px", // Adjust padding for the dropdown indicator
    }),
    input: (provided) => ({
        ...provided,
        maxWidth: "3vw",
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        height: "30px",
    }),
};

const isCompanyUser = userCompanyId();
const _isCounterUser = userCounterId();

const getAndSetCounterByCompany = async (
    companyId,
    searchOptions,
    setSearchOptions
) => {
    try {
        if (isCompanyUser && companyId) {
            const response = await getCounters({ company: companyId });
            let counters = response.data.data.items ?? [];

            if (counters.length) {
                counters = counters
                    .map((item) => ({
                        label: item.title,
                        value: item.id,
                    }))
                    .sort((a, b) =>
                        a.label
                            ?.toLowerCase()
                            ?.localeCompare(b.label?.toLowerCase())
                    );

                setSearchOptions({ ...searchOptions, counters });
            }
        }
    } catch (e) {
        console.log(e);
    }
};

const getAndSetUsersByCounter = async (
    counterId,
    companyId,
    searchOptions,
    setSearchOptions,
    setIsCounterUserLoading
) => {
    setIsCounterUserLoading(true);

    try {
        if (counterId) {
            const response = await getCounterUsers({
                "details.counter_id": _isCounterUser ?? counterId,
                "details.company_id": isCompanyUser ?? companyId,
                isActive: true,
                per_page: 50,
            });

            const users = response.data.data.results ?? [];

            if (users.length) {
                const counterUsers = users
                    .map((item) => ({
                        label: item.username,
                        value: item.id,
                        config: item.config,
                    }))
                    .sort((a, b) =>
                        a.label
                            ?.toLowerCase()
                            ?.localeCompare(b.label?.toLowerCase())
                    );

                setSearchOptions({ ...searchOptions, counterUsers });
            }
        }
    } catch (e) {
        console.log(e);
    }

    setIsCounterUserLoading(false);
};

const getDistrictId = (stations = [], stationId) => {
    for (const station of stations) {
        if (station.id == stationId) {
            return station.district_id;
        }
    }

    return null;
};

const getDistricts = (stations, toStationIds) => {
    const toDistricts = [...toStationIds];
    for (const toStation of toStationIds) {
        const districtId = getDistrictId(stations, toStation);
        if (districtId && !toDistricts.includes(`dis_${districtId}`)) {
            toDistricts.push(`dis_${districtId}`);
        }
    }

    return toDistricts;
};

const getStation = (stations = [], stationId = null) => {
    for (const station of stations) {
        if (station.value == stationId) {
            return station;
        }
    }

    return null;
};

const getToStations = (stations = [], toStationIds, fromStaionId) => {
    const toStations = [];
    stations.forEach((item) => {
        if (toStationIds.includes(item.value) && fromStaionId != item.value)
            toStations.push(item);
    });

    return toStations;
};

const processStations = (stations = [], travelMode = "bus") => {
    // ToDo: process same formStaion exist in toStations
    try {
        const uniqueDistricts = {};
        stations = stations
            .filter(({ travel_mode }) => travel_mode === travelMode)
            .map((station) => {
                const isDistrictExist = uniqueDistricts[station.district_id]
                    ? true
                    : false;

                if (isDistrictExist) {
                    const oldToStations =
                        uniqueDistricts[station.district_id].toStations;
                    const newToStations = [];

                    for (const newTo of station.to) {
                        let isFound = false;
                        for (const existingTo of oldToStations) {
                            if (newTo === existingTo) {
                                isFound = true;
                                break;
                            }
                        }

                        if (!isFound) {
                            const districtId = getDistrictId(stations, newTo);
                            if (
                                districtId &&
                                !oldToStations.includes(`dis_${districtId}`) &&
                                !newToStations.includes(`dis_${districtId}`)
                            ) {
                                // Add new to district.
                                newToStations.push(`dis_${districtId}`);
                            }

                            // Add new to station.
                            newToStations.push(newTo);
                        }
                    }

                    uniqueDistricts[station.district_id].toStations = [
                        ...oldToStations,
                        ...newToStations,
                    ];
                } else {
                    const toDistricts = getDistricts(stations, station.to);

                    uniqueDistricts[station.district_id] = {
                        label: station.district,
                        value: `dis_${station.district_id}`,
                        toStations: toDistricts,
                        type: "district",
                        originalId: station.district_id,
                    };
                }

                let toStations = [];

                if (isDistrictExist) {
                    const toDistricts = getDistricts(stations, station.to);
                    toStations = toDistricts;
                } else {
                    toStations = [
                        ...uniqueDistricts[station.district_id].toStations,
                    ];
                }

                return {
                    label: station.title,
                    value: station.id,
                    originalId: station.id,
                    toStations,
                    type: "station",
                };
            });

        let finalStations = [...Object.values(uniqueDistricts), ...stations];
        finalStations.sort((a, b) => {
            if (a.label < b.label) return -1;
            if (a.label > b.label) return 1;
            return 0;
        });

        return finalStations;
    } catch (e) {}

    return [];
};

const getCounterUserBalance = async (userId) => {
    try {
        const balance = await getBalance({ user_id: userId });
        if (
            balance &&
            balance?.data?.data?.current_balance &&
            balance?.data?.data?.currency
        ) {
            return balance?.data?.data;
        }
    } catch (e) {
        console.log(e);
    }

    return { current_balance: 0, currency: "BDT" };
};

export {
    getTopNavbarMenus,
    getAndSetUsersByCounter,
    getAndSetCounterByCompany,
    getCounterUserBalance,
    processStations,
    getStation,
    getToStations,
    TripSearchModel,
    TripSearchDropdownDefaultOptions,
    tripSearchSelectInputStyle,
};
